import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import AppLayout from "@/components/AppLayout";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import ProfilePictureUpload from "@/components/shared/ProfilePictureUpload";
import LocationCombobox from "@/components/shared/LocationCombobox";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

export default function EditProfile() {
  const { id } = useParams();
  const nav = useNavigate();
  const { refresh } = useAuth();
  const [p, setP] = useState(null);
  const [nf, setNf] = useState(false);
  const [location, setLocation] = useState(null);

  useEffect(() => {
    api.get(`/profiles/${id}`).then(({ data }) => {
      setP(data);
      if (data.location_city && data.location_state) {
        setLocation({ city: data.location_city, state: data.location_state, label: data.location_label });
      }
    }).catch(() => setNf(true));
  }, [id]);

  const save = async () => {
    if (!location) { toast.error("Location is required"); return; }
    try {
      await api.patch(`/profiles/${id}`, {
        display_name: p.display_name,
        location_city: location.city, location_state: location.state,
        bio: p.bio, profile_picture_url: p.profile_picture_url,
        youtube_url: p.youtube_url || null, instagram_url: p.instagram_url || null,
        soundcloud_url: p.soundcloud_url || null,
        whatsapp_number: p.whatsapp_number || null,
      });
      toast.success("Saved");
      await refresh();
      nav("/profiles");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  if (nf) return <AppLayout><div className="py-20 text-center text-slate-500">Not found.</div></AppLayout>;
  if (!p) return <AppLayout><div className="py-16 text-center text-slate-500">Loading…</div></AppLayout>;

  return (
    <AppLayout>
      <h1 className="font-display text-3xl font-bold tracking-tight">Edit profile</h1>
      <Card className="mt-6 p-6 border-slate-200 max-w-3xl">
        <div>
          <Label>Profile picture</Label>
          <div className="mt-2"><ProfilePictureUpload value={p.profile_picture_url} onChange={(v) => setP({ ...p, profile_picture_url: v })} /></div>
        </div>
        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div><Label>Display name</Label><Input value={p.display_name || ""} onChange={(e) => setP({ ...p, display_name: e.target.value })} /></div>
          <div>
            <Label>Location</Label>
            <div className="mt-1"><LocationCombobox value={location} onChange={setLocation} testId="edit-location" /></div>
          </div>
        </div>
        <div className="mt-4"><Label>Bio</Label><Textarea rows={3} value={p.bio || ""} onChange={(e) => setP({ ...p, bio: e.target.value })} /></div>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
          <Input placeholder="YouTube URL" value={p.youtube_url || ""} onChange={(e) => setP({ ...p, youtube_url: e.target.value })} />
          <Input placeholder="Instagram URL" value={p.instagram_url || ""} onChange={(e) => setP({ ...p, instagram_url: e.target.value })} />
          <Input placeholder="SoundCloud URL" value={p.soundcloud_url || ""} onChange={(e) => setP({ ...p, soundcloud_url: e.target.value })} />
        </div>
        <div className="mt-4">
          <Input
  placeholder="WhatsApp (with country code)"
  value={p.whatsapp_number || ""}
  onChange={(e) =>
    setP({
      ...p,
      whatsapp_number: e.target.value.replace(/\D/g, ""),
    })
  }
  inputMode="numeric"
  pattern="[0-9]*"
/>
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <Button variant="ghost" onClick={() => nav(-1)}>Cancel</Button>
          <Button onClick={save} data-testid="edit-save" className="bg-[#4F46E5] hover:bg-[#4338CA] text-white">Save</Button>
        </div>
      </Card>
    </AppLayout>
  );
}
